import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../../hooks/useRetryFetch";
import useAuth from "../../hooks/useAuth";
import { config } from "../../Environment";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { decimalAmount, currencyAmount } from "../../lib/currency";
import { PaymentIframe } from "../payment/PaymentIframe";
import { CreditCard, Lock, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "../ui/card";

const BuyService = ({ processId, currentActivity, itemkey, content = {}, userData = {}, onChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const { t } = useTranslation();
  const { auth, setAuth } = useAuth();

  /**
   * The service price is known to the server but is associated to the button
   * instead of the product. The buy service component api knows to look for the
   * price on the button so we just need to send the button id to the server.
   */
  async function buyService() {
    try {
      setIsLoading(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/component/buy-service`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            activity: currentActivity,
            componentId: content.key,
            useIframe: true,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();

        if (data.type === 'redirect') {
          onChange(content.key, { ...content, status: 'pending' });
          setPaymentUrl(data.redirect);
          setShowPaymentIframe(true);
        }
      } else {
        toast.error(t("payment_initialization_failed") || "Failed to initialize payment");
      }
    } catch (err) {
      console.log(`Buy service failed: ${err.message}`);
      toast.error(t("payment_failed") || "Failed to start payment");
    } finally {
      setIsLoading(false);
    }
  }

  const retryFetch = useRetryFetch();

  const handlePaymentSuccess = (data) => {
    console.log("[BuyService] Payment successful:", data);
    setShowPaymentIframe(false);
    toast.success(t("payment_successful") || "Payment successful!");
    // Update the component status to 'paid'
    onChange(content.key, { ...content, status: 'paid' });
  };

  const handlePaymentError = (error) => {
    console.error("[BuyService] Payment error:", error);
    toast.error(error || t("payment_failed") || "Payment failed");
    setShowPaymentIframe(false);
    // Reset status to allow retry
    onChange(content.key, { ...content, status: undefined });
  };

  const handlePaymentClose = () => {
    setShowPaymentIframe(false);
  };

  // Render different states
  if (userData.status === 'paid') {
    return (
      <div className="mt-6">
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-1 flex items-center gap-2">
                  {t("payment_completed") || "Payment Completed"}
                </h3>
                <p className="text-sm text-green-700 mb-3">
                  {t("payment_successful_description") || "Your payment has been processed successfully. You can continue with the service."}
                </p>
                {content.amount && content.currency && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-green-200">
                    <span className="text-xs text-gray-600">{t("paid") || "Paid"}:</span>
                    <span className="text-sm font-bold text-green-700">
                      {currencyAmount(content.amount, content.currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="mt-6">
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-indigo-50 to-purple-50 shadow-lg">
          <CardContent className="p-0">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-primary to-indigo-600 px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {t("ready_to_purchase") || "Service Payment Required"}
                  </h3>
                  <p className="text-xs text-primary-foreground/80">
                    {t("secure_payment") || "Secure payment processing"}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-6">
              <p className="text-sm text-gray-700">
                {t("service_payment_description") || "Complete payment to continue with this service"}
              </p>

              {/* Price Display */}
              {content.amount && content.currency && (
                <div className="bg-white rounded-xl p-5 border-2 border-primary/20 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        {t("service_fee") || "Service Fee"}
                      </p>
                      <p className="text-3xl font-bold bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent">
                        {currencyAmount(content.amount, content.currency)}
                      </p>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/8 to-indigo-100 flex items-center justify-center">
                      <CreditCard className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Security badges */}
              <div className="flex items-center gap-4 text-xs text-gray-600 bg-white/60 rounded-lg p-3 border border-primary/10">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-green-600" />
                  <span>{t("secure_checkout") || "Secure checkout"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                  <span>{t("encrypted") || "Encrypted"}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button
                  onClick={() => buyService()}
                  disabled={!auth.email || isLoading}
                  className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-primary to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t("processing") || "Processing..."}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      {t("proceed_to_payment") || "Proceed to Payment"}
                    </>
                  )}
                </Button>
              </div>

              {!auth.email && (
                <div className="text-center">
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {t("login_required_for_payment") || "Please log in to complete your purchase"}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <PaymentIframe
        open={showPaymentIframe}
        paymentUrl={paymentUrl}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onClose={handlePaymentClose}
      />
    </>
  );
};

export default BuyService;
